import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const UpdateDefaultHtml = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getUrlByOperation } = useApiUrls();

  const [htmlDocList, setHtmlDocList] = useState([]);
  const [htmlDocZipList, setHtmlDocZipList] = useState([]);

  // Selection Mode: "doc" for HTML Document, "zip" for HTML Doc Zip
  const [selectMode, setSelectMode] = useState("doc");
  const [htmlDocId, setHtmlDocId] = useState("0");
  const [htmlDocZipId, setHtmlDocZipId] = useState("0");

  const [groupInfo, setGroupInfo] = useState("");
  const [subGroupInfo, setSubGroupInfo] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const token = getCookie("token");
  const userid = getCookie("userid") || "1";

  useEffect(() => {
    const fetchData = async () => {
      // 1. Fetch Default HTML Details by ID
      try {
        const getUrl =
          getUrlByOperation("DefaultHtmlGet") ||
          "https://api.dqr-display-tms.eventum.co.in/api/DefaultHtmlOperation/Get";

        const res = await axios.post(
          getUrl,
          { id: parseInt(id) },
          {
            headers: {
              accept: "text/plain",
              UserId: userid,
              "X-Authorization": token,
              "Content-Type": "application/json-patch+json",
            },
          }
        );

        if (res.data) {
          const itemData =
            res.data.data && Array.isArray(res.data.data) && res.data.data[0]
              ? res.data.data[0]
              : res.data.data || res.data;

          if (itemData) {
            const docVal = itemData.htmldocid !== undefined ? String(itemData.htmldocid) : "0";
            const zipVal = itemData.htmldoczipid !== undefined ? String(itemData.htmldoczipid) : "0";

            if (Number(zipVal) > 0 && Number(docVal) === 0) {
              setSelectMode("zip");
              setHtmlDocZipId(zipVal);
              setHtmlDocId("0");
            } else {
              setSelectMode("doc");
              setHtmlDocId(docVal);
              setHtmlDocZipId("0");
            }

            if (itemData.groupname || itemData.groupid) {
              setGroupInfo(itemData.groupid === -1 ? "All" : itemData.groupname || `Group ID: ${itemData.groupid}`);
            }
            if (itemData.subgroupname || itemData.subgroupid) {
              setSubGroupInfo(itemData.subgroupid === -1 ? "All" : itemData.subgroupname || `SubGroup ID: ${itemData.subgroupid}`);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching default html details:", err);
      }

      // 2. Fetch HTML Documents
      try {
        const docUrl =
          "https://api.dqr-display-tms.eventum.co.in/api/HtmlDocOperation/GetDistinct/name";
        const docRes = await axios.get(docUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (docRes.data && docRes.data.isresponse && Array.isArray(docRes.data.data)) {
          setHtmlDocList(docRes.data.data);
        } else if (docRes.data && Array.isArray(docRes.data.data)) {
          setHtmlDocList(docRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching html docs:", err);
      }

      // 3. Fetch HTML Doc Zips
      try {
        const zipUrl =
          "https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/GetDistinct/name";
        const zipRes = await axios.get(zipUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (zipRes.data && zipRes.data.isresponse && Array.isArray(zipRes.data.data)) {
          setHtmlDocZipList(zipRes.data.data);
        } else if (zipRes.data && Array.isArray(zipRes.data.data)) {
          setHtmlDocZipList(zipRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching html doc zips:", err);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, getUrlByOperation, token, userid]);

  const handleModeChange = (mode) => {
    setSelectMode(mode);
    if (mode === "doc") {
      setHtmlDocZipId("0");
    } else {
      setHtmlDocId("0");
    }
  };

  const handleHtmlDocChange = (e) => {
    const val = e.target.value;
    setHtmlDocId(val);
    if (val !== "0") {
      setSelectMode("doc");
      setHtmlDocZipId("0");
    }
  };

  const handleHtmlDocZipChange = (e) => {
    const val = e.target.value;
    setHtmlDocZipId(val);
    if (val !== "0") {
      setSelectMode("zip");
      setHtmlDocId("0");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const finalDocId = selectMode === "doc" ? Number(htmlDocId) || 0 : 0;
    const finalZipId = selectMode === "zip" ? Number(htmlDocZipId) || 0 : 0;

    if (finalDocId === 0 && finalZipId === 0) {
      setMessage("Please select either HTML Document or HTML Doc Zip.");
      setMessageType("error");
      setLoading(false);
      return;
    }

    const payload = {
      htmldocid: finalDocId,
      htmldoczipid: finalZipId,
      id: Number(id),
    };

    try {
      const updateUrl =
        getUrlByOperation("DefaultHtmlUpdate") ||
        "https://api.dqr-display-tms.eventum.co.in/api/DefaultHtmlOperation/Update";

      const response = await axios.post(updateUrl, payload, {
        headers: {
          accept: "text/plain",
          UserId: userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
      });

      if (
        response.data &&
        (response.data.isresponse === true || response.data.responsestatus === "SUCCESS" || response.data.status === 200)
      ) {
        setMessage(response.data?.errormsg || "Default HTML updated successfully!");
        setMessageType("success");
        setTimeout(() => {
          navigate("/default-html");
        }, 1500);
      } else {
        const errStr =
          response.data?.errormsg ||
          response.data?.message ||
          response.data?.responsestatus ||
          "Failed to update Default HTML.";
        setMessage(errStr);
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error updating Default HTML:", error);
      const errStr =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "Failed to update Default HTML.";
      setMessage(errStr);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/default-html");
  };

  const getItemValue = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.id !== undefined ? item.id : item.value !== undefined ? item.value : item.htmldocid || item.htmldoczipid || 0;
    }
    return item;
  };

  const getItemLabel = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.value || item.name || item.htmldocname || item.id || "Option";
    }
    return item;
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Update Default HTML</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <a href="/" onClick={(e) => { e.preventDefault(); navigate("/default-html"); }}>
                        Default HTML
                      </a>
                    </li>
                    <li className="breadcrumb-item active">Update</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Edit Default HTML (ID: {id})</h3>
                    </div>
                    <form onSubmit={handleSubmit}>
                      <div className="card-body">
                        {message && messageType === "error" && (
                          <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => setMessage("")}>&times;</button>
                            <h5><i className="icon fas fa-ban"></i> Error</h5>
                            {message}
                          </div>
                        )}

                        {(groupInfo || subGroupInfo) && (
                          <div className="row mb-3">
                            {groupInfo && (
                              <div className="col-md-6">
                                <label>Group Info</label>
                                <input className="form-control" value={groupInfo} disabled />
                              </div>
                            )}
                            {subGroupInfo && (
                              <div className="col-md-6">
                                <label>SubGroup Info</label>
                                <input className="form-control" value={subGroupInfo} disabled />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Document Type Selector (Mutually Exclusive) */}
                        <div className="row mt-2 mb-3">
                          <div className="col-md-12">
                            <label className="d-block">Select Document Type (Either HTML Document or HTML Doc Zip)</label>
                            <div className="custom-control custom-radio custom-control-inline">
                              <input
                                type="radio"
                                id="modeDocEdit"
                                name="docTypeModeEdit"
                                className="custom-control-input"
                                checked={selectMode === "doc"}
                                onChange={() => handleModeChange("doc")}
                              />
                              <label className="custom-control-label" htmlFor="modeDocEdit">
                                HTML Document
                              </label>
                            </div>
                            <div className="custom-control custom-radio custom-control-inline">
                              <input
                                type="radio"
                                id="modeZipEdit"
                                name="docTypeModeEdit"
                                className="custom-control-input"
                                checked={selectMode === "zip"}
                                onChange={() => handleModeChange("zip")}
                              />
                              <label className="custom-control-label" htmlFor="modeZipEdit">
                                HTML Doc Zip
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Dropdowns */}
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>HTML Document</label>
                              <select
                                className="form-control"
                                value={selectMode === "doc" ? htmlDocId : "0"}
                                onChange={handleHtmlDocChange}
                                disabled={selectMode !== "doc"}
                              >
                                <option value="0">-- Select HTML Document --</option>
                                {htmlDocList &&
                                  htmlDocList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>HTML Doc Zip</label>
                              <select
                                className="form-control"
                                value={selectMode === "zip" ? htmlDocZipId : "0"}
                                onChange={handleHtmlDocZipChange}
                                disabled={selectMode !== "zip"}
                              >
                                <option value="0">-- Select HTML Doc Zip --</option>
                                {htmlDocZipList &&
                                  htmlDocZipList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer">
                        <button
                          type="submit"
                          className="btn btn-info"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default ml-2"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message && messageType === "success" && (
        <SuccessMessage message={message} onClose={() => setMessage("")} />
      )}
      {message && messageType === "error" && (
        <FailedMessage message={message} onClose={() => setMessage("")} />
      )}
    </>
  );
};

export default UpdateDefaultHtml;
